import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { FileKey } from '@/types/bundle'

// The one shared post-save refresh step: which cached queries a successful
// write makes stale. Every draft write can change what Diff & Validation
// shows, so the diff is refreshed by every draft-changing kind - without
// it that screen's cached ['diff', ...] query can keep showing an edit that
// was since reverted.
//
// Each affected query that no screen is showing right now is dropped from
// the cache, and each one that is showing is refetched. A screen opened
// afterwards therefore loads fresh data instead of first rendering - and
// seeding its editable copy from - the old cached one. A screen still
// showing the data follows the refetch through hooks/useServerSeed.ts.
//
// Call it only once the write has succeeded (onSuccess, or right after an
// awaited request), never on failure.
export type DraftWrite =
  // Any PUT or DELETE of one config file (save, list-screen delete, Diff &
  // Validation's per-file discard and per-field revert).
  | { kind: 'draft-file'; fileKey: FileKey }
  // Prompts screen save or delete of one prompts/*.txt file.
  | { kind: 'prompt-file'; filename: string }
  // Product/Document Type detail: their own prompt PUT, or the delete after
  // clearing it. Does not refresh validate-draft (the config save that goes
  // with it does, on success).
  | { kind: 'embedded-prompt'; filename: string }
  // Diff & Validation "Discard all" (every changed file plus every prompt).
  | { kind: 'discard-all' }
  // Diff & Validation per-prompt discard.
  | { kind: 'discard-prompt' }
  | { kind: 'publish' }
  // Repointing live never touches the draft itself, but every file and
  // prompt without a draft reads through to the live bundle, which changed
  // (Frontend PRD 11).
  | { kind: 'go-live' }

// Keys are prefixes (React Query's default non-exact match): ['diff']
// covers every ['diff', from, to], and ['draft-prompt'] every prompt file
// (but not ['draft-prompts'], the list).
const REFRESH_TABLE: { [K in DraftWrite['kind']]: (write: Extract<DraftWrite, { kind: K }>) => QueryKey[] } = {
  'draft-file': ({ fileKey }) => [['draft-file', fileKey], ['draft-manifest'], ['validate-draft'], ['diff']],
  'prompt-file': ({ filename }) => [['draft-prompt', filename], ['draft-prompts'], ['validate-draft'], ['diff']],
  'embedded-prompt': ({ filename }) => [['draft-prompt', filename], ['draft-prompts'], ['diff']],
  'discard-all': () => [
    ['draft-file'],
    ['diff'],
    ['validate-draft'],
    ['draft-manifest'],
    ['draft-prompt'],
    ['draft-prompts'],
  ],
  'discard-prompt': () => [['diff'], ['draft-manifest'], ['draft-prompt'], ['draft-prompts']],
  // Publish removes every draft, so every file and prompt now reads through
  // to live.
  publish: () => [
    ['bundles'],
    ['draft-manifest'],
    ['diff', 'live', 'draft'],
    ['validate-draft'],
    ['draft-file'],
    ['draft-prompt'],
    ['draft-prompts'],
  ],
  'go-live': () => [['bundles'], ['draft-file'], ['draft-prompt'], ['draft-prompts']],
}

// Fire-and-forget on purpose: returning the refetch promise from a
// mutation's onSuccess would keep the mutation pending (and its Save button
// disabled) until every refetch finished. A refetch that fails shows on
// that query, never as a save error.
export function refreshAfterWrite(queryClient: QueryClient, write: DraftWrite): void {
  const keys = (REFRESH_TABLE[write.kind] as (write: DraftWrite) => QueryKey[])(write)
  for (const queryKey of keys) {
    queryClient.removeQueries({ queryKey, type: 'inactive' })
    void queryClient.invalidateQueries({ queryKey })
  }
}
